import clsx from "~/util/html/style";
import { mergeProps, type JSX, Show, type ComponentProps, splitProps } from "solid-js";
import { Icon, type IconName } from "~/icon";

type BannerVariant = "info" | "success" | "warning" | "error";

type CustomProps = {
  title?: JSX.Element;
  description?: JSX.Element;
  /** Custom children content (takes precedence over description, rendered without wrapper) */
  children?: JSX.Element;
  variant?: BannerVariant;
  /** Adds AI glow effect (can be combined with any variant) */
  ai?: boolean;
  iconName?: IconName;
  border?: boolean;
  actions?: JSX.Element;
  class?: string;
  size?: "sm" | "md";
  /** "bar": a full-width 48px strip with a bottom border and square corners, its content centered,
   *  for a notice across the top of a page. Defaults to the rounded card. */
  layout?: "card" | "bar";
};

type BannerProps = Omit<ComponentProps<"div">, keyof CustomProps> & CustomProps;

export function Banner(_props: BannerProps) {
  const props = mergeProps({ variant: "info" }, _props);
  const [local, rest] = splitProps(props, [
    "title",
    "description",
    "children",
    "variant",
    "ai",
    "iconName",
    "border",
    "actions",
    "class",
    "size",
    "layout",
  ]);

  return (
    <div
      class={clsx(
        // base
        "display-flex flex items-center",

        // a page-wide bar, a compact card, or the default card
        local.layout === "bar"
          ? "h-12 shrink-0 justify-center gap-2 border-b px-4 text-sm"
          : local.size === "sm"
            ? "gap-3 rounded-lg px-2 py-1"
            : "gap-3 rounded-xl px-4 py-3",

        // border
        local.border && "border",

        // variants
        local.variant === "info" && "border-brand-border bg-brand-light",
        local.variant === "success" && "border-brand-success bg-brand-green",
        local.variant === "warning" && "border-brand-warning bg-brand-warning-light",
        local.variant === "error" && "border-brand-danger bg-brand-danger-light text-brand-danger",

        local.ai &&
          "transform-3d before:absolute before:inset-0 before:transform-3d-behind before:rounded-xl before:bg-linear-to-r before:from-indigo-400 before:to-purple-400 before:blur-xs",

        // custom
        local.class,
      )}
      {...rest}
    >
      <Show when={local.iconName}>
        {(iconName) => <Icon name={iconName()} size={local.layout === "bar" ? "md" : "lg"} />}
      </Show>

      <div
        class={clsx("flex flex-col gap-1", local.layout === "bar" ? "min-w-0" : "mr-auto w-full")}
      >
        <Show when={local.title}>
          <p class="text-sm font-bold">{local.title}</p>
        </Show>

        <Show
          when={local.children}
          fallback={
            <Show when={local.description}>
              <p class="text-xs whitespace-pre-wrap text-typography-secondary">
                {local.description}
              </p>
            </Show>
          }
        >
          {local.children}
        </Show>
      </div>

      <Show when={local.actions}>
        <div class="flex gap-3">{local.actions}</div>
      </Show>
    </div>
  );
}
